// @ts-strict-ignore
import { setProperty, deleteProperty, getProperty } from 'dot-prop';
import { v4 as uuidv4 } from 'uuid';
import {
  getNumericPathIndices, getActionName, setActionName, computeNextActionName,
  isActionDataObjectEphemeral,
} from 'flow/service/actions';
import { createAction } from '../../engine/create/actions/actions';
import type { CreateActionPayload } from '../../engine/create/actions/types';
import {
  modifyRelevantDataFromActionRemoval,
  modifyRelevantDataFromActionUpdate,
} from '../../engine/update/actions';
import {
  snapshotAndCloneWorkflowData,
  compileWorkflowDataDependencies,
  getParentPathAndExpressionItem,
} from '../helpers';
import {
  groupUniqueActionNamesByType,
  isActionOfType,
} from 'flow/service/actions/actions';
import type { WorkflowState } from '../state';
import { WorkflowEngineException } from '../../engine/schema';
import type {
  Action,
  DataRoute,
  Expression,
  ExpressionTerm,
  Threshold,
} from '../../engine/types';
import {
  OperationMessageType,
} from '../../engine/types';
import { correctAmbiguousBooleanRule } from '../../engine/update/rules/rules';
import { ReduxErrorTypes } from '../constants';
import { RuleUpdateOperationType } from '../../engine/update/constants';
import type { PendingParenthesisInfo } from 'flow/editor/components/useCaretControl/types';
import { getGroupStartIndexOffset } from 'flow/editor/components/useCaretControl/helpers';
import { ACTION_TYPES_ALLOWING_NAME_COLLISIONS } from 'flow/service/actions/constants';
import { modifyRelevantPropsFromActionRemoval } from 'flow/store/engine/update/props';
import { ActionType } from 'flow/store/engine/constants';


// ================ Action Reducers ================

/**
 * Reducer to add an Action with optional Thresholds and Data Routes.
 * Performs minimal validation on the action.
 */
export function addAction(
  state: WorkflowState,
  action: {
    payload: {
      action: Action;
      thresholds?: Threshold[];
      dataRoutes?: DataRoute[];
    };
  },
) {
  const currentActions = state.workflowData.actions;
  const { thresholds, dataRoutes } = action.payload;

  let actionToAdd = structuredClone(action.payload.action);
  const nextActionName = computeNextActionName(actionToAdd, currentActions);
  if (getActionName(actionToAdd) !== nextActionName) {
    actionToAdd = setActionName(actionToAdd, nextActionName);
  }


  state.workflowData.actions = setProperty(currentActions, actionToAdd.publicId, actionToAdd);
  thresholds?.forEach(threshold => {
    // defend against thresholds not belonging to the action
    if (threshold.actionId !== actionToAdd.publicId) {
      return;
    }

    // Skip thresholds with matrix model RHS actions when cloning
    // Matrix models cannot be used as right-hand side actions in dynamic comparisons
    if (threshold.right.actionId) {
      const rightAction = state.workflowData.actions[threshold.right.actionId];
      if (rightAction && isActionOfType(rightAction, [
        ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
      ])) {
        return;
      }
    }

    state.workflowData.thresholds = setProperty(
      state.workflowData.thresholds,
      threshold.publicId,
      threshold);
  });

  dataRoutes?.forEach(dataRoute => {
    // defend against dataRoute not belonging to the action
    if (dataRoute.targetId !== actionToAdd.publicId) {
      return;
    }

    state.workflowData.dataRoutes = setProperty(
      state.workflowData.dataRoutes,
      dataRoute.publicId,
      dataRoute,
    );
  });
}


/**
 * Reducer to create workflow Actions from a payload.
 * This function ensures that no duplicate actions are created, and supports
 * any type of action
 */
export function createActions(
  state: WorkflowState,
  action: { payload: CreateActionPayload | CreateActionPayload[] },
) {
  const {
    payload,
  } = action;

  const {
    workflowData: { actions: existingActions }, workflowSnapshots,
  } = snapshotAndCloneWorkflowData(state);

  const uniqueActionNamesByType = groupUniqueActionNamesByType(
    existingActions,
  );

  let updatedActions = existingActions;
  const createActionPayloads = Array.isArray(payload) ? payload : [payload];

  createActionPayloads
    .forEach(createActionPayload => {
      const newAction = createAction(createActionPayload);
      const name = getActionName(newAction);
      const { publicId: path, type } = newAction;

      // Prevents creating duplicate actions
      if (!ACTION_TYPES_ALLOWING_NAME_COLLISIONS.includes(type)) {
        // early return if the action being added already exists (compared using the action's name)
        if (uniqueActionNamesByType.get(type)?.has(name)) {
          return;
        }

        // add a new Set for the action type if this is the first action of that type being added
        if (!uniqueActionNamesByType.has(type)) {
          uniqueActionNamesByType.set(type, new Set());
        }

        uniqueActionNamesByType.get(type).add(name);
      }

      updatedActions = setProperty(existingActions, path, newAction);
    });

  state.workflowData.actions = updatedActions;
  state.workflowSnapshots = workflowSnapshots;
}


export function removeAction(
  state: WorkflowState,
  action: {
    payload: { id: string };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { id } = action.payload;
  const originalAction = workflowData.actions[id];
  if (!originalAction) return;
  deleteProperty(workflowData.actions, id);
  if (isActionDataObjectEphemeral(originalAction)) {
    modifyRelevantPropsFromActionRemoval({
      actionToRemove: originalAction,
      workflowProps: state.workflowProps,
      workflowActions: workflowData.actions,
    });
  }
  modifyRelevantDataFromActionRemoval({ originalTargetValue: originalAction, workflowData });
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function updateAction(
  state: WorkflowState,
  action: {
    payload: {
      path: string;
      value: any;
      skipSnapshotAndDependencies?: boolean;
    };
  },
) {
  const { path, value, skipSnapshotAndDependencies = false } = action.payload;

  if (skipSnapshotAndDependencies) {
    state.workflowData.actions = setProperty(state.workflowData.actions, path, value);
  } else {
    const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
    modifyRelevantDataFromActionUpdate({ path, value, workflowData });
    workflowData.actions = setProperty(workflowData.actions, path, value);
    state.workflowData = compileWorkflowDataDependencies(workflowData);
    state.workflowSnapshots = workflowSnapshots;
  }
}

export function clearActionRulesOperatorAmbiguousMode(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      newRuleOperatorForCorrection: 'and' | 'or';
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { id, newRuleOperatorForCorrection } = action.payload;

  const rules = getProperty(workflowData.actions, `${id}.rules`);
  const cleanedRules = correctAmbiguousBooleanRule({
    newRuleOperatorForCorrection,
    originalRule: rules,
  });
  workflowData.actions = setProperty(workflowData.actions, `${id}.rules`, cleanedRules);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;

  state.workflowOperationDescriptions.push({
    id: uuidv4(),
    publicId: id,
    type: OperationMessageType.actionRuleThreshold,
    timestamp: Date.now(),
    message: RuleUpdateOperationType.AUTO_CORRECTED_AMBIGUOUS_OPERATOR_RULE,
    data: undefined,
  });
}

export function createActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      path: string;
      value: any;
      insertion?: boolean;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const {
    id, path, value, insertion,
  } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.OUTPUT_EXPRESSION_NOT_FOUND);
  }

  if (insertion && path) { // we are going to insert an item
    const indices = getNumericPathIndices(path); // grab only digits from the path string
    if (indices.length === 1) { // path points to 1-dimensional array's index (expression array doesnt have nested arrays)
      const expressionItemOnPath: Expression = getProperty(existingExpression, path);

      if (Array.isArray(expressionItemOnPath) && !expressionItemOnPath.length) {
        // oh this is group and if path is simply [number], it means this group is new and empty
        (existingExpression[Number(indices[0])] as ExpressionTerm[]).push(value); // insert new one in the array
      } else {
        existingExpression.splice(Number(indices[0]) + 1, 0, value); // insert new one at the index next to where the path was pointing at.
      }
    } else {
      const {
        pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
      } = getParentPathAndExpressionItem({
        indices,
        existingExpression,
      });
      parentExpressionArrayOfTheExpressionOnTheLastIndex.splice(
        Number(indices.at(-1)) + 1, 0, value); // insert new one at the index next to where the path was pointing at.

      // and now overwrite the parent expression array
      setProperty(
        existingExpression,
        pathWithoutLastIndex,
        parentExpressionArrayOfTheExpressionOnTheLastIndex,
      );
    }

    workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  } else if (existingExpression.length > 0 && !path) {
    // in this case, path is '', but there is an expression already; meaning user tried adding a new item at the very beginning.
    const updatedExpression = [value, ...existingExpression];
    workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, updatedExpression);
  } else { // we are gonna append an item
    let indexPath = `[${existingExpression.length}]`;
    if (path) {
      const indices = getNumericPathIndices(path);
      indices[indices.length - 1] = String(Number(indices.at(-1)) + 1);
      indexPath = indices.reduce((result, indexValue) => `${result}[${indexValue}]`, '');
    }
    workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression${indexPath}`, value);
  }

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function updateActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
      value: any;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id, value } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression${expressionPath}`, value);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function removeActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const indices = getNumericPathIndices(expressionPath); // grab only digits from the path string
  if (indices.length === 1) {
    existingExpression.splice(Number(indices[0]), 1);
  } else {
    const {
      pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
    } = getParentPathAndExpressionItem({
      indices,
      existingExpression,
    });
    parentExpressionArrayOfTheExpressionOnTheLastIndex.splice(
      Number(indices.at(-1)), 1);
    setProperty(
      existingExpression,
      pathWithoutLastIndex,
      parentExpressionArrayOfTheExpressionOnTheLastIndex,
    );
  }

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function groupActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      groupOpeningInfo: PendingParenthesisInfo;
      groupClosingPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { groupOpeningInfo, groupClosingPath, id } = action.payload;

  let existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const { path, side } = groupOpeningInfo;
  // grab only digits from the path string
  const openingPathIndices = getNumericPathIndices(path);
  const closingPathIndices = getNumericPathIndices(groupClosingPath);

  // When we set a paranthesis to be before an expression item,
  // we want to ensure that we don't remove the expression item it precedes
  const groupOpenIndexOffset = getGroupStartIndexOffset(side);
  const openingGroupIndex = Number(openingPathIndices.at(-1)) + groupOpenIndexOffset;
  const closingGroupIndex = Number(closingPathIndices.at(-1)) + 1;

  if (openingPathIndices.length === 1) {
    // expression is flat 1-dimen array
    const rangeOfItemsFromPath = existingExpression.slice(
      openingGroupIndex, closingGroupIndex,
    ) as ExpressionTerm[];

    // spread what was in the group into the existing expression, at the index where the group was located at
    existingExpression = [
      ...existingExpression.slice(0, openingGroupIndex),
      rangeOfItemsFromPath,
      ...existingExpression.slice(closingGroupIndex) || [],
    ];
  } else {
    const {
      pathWithoutLastIndex,
      parentExpressionArrayOfTheExpressionOnTheLastIndex: parentExpressionArray,
    } = getParentPathAndExpressionItem({
      indices: openingPathIndices,
      existingExpression,
    });

    // remove and retrieve "cut" the items in the range of group opening path / closing path
    const rangeOfItemsFromPath = parentExpressionArray.slice(
      openingGroupIndex, closingGroupIndex,
    );

    // spread what was in the group into the existing expression, at the index where the group was located at
    const updatedParentExpressionArray = [
      ...parentExpressionArray.slice(0, openingGroupIndex),
      rangeOfItemsFromPath,
      ...parentExpressionArray.slice(closingGroupIndex) || [],
    ];

    setProperty(
      existingExpression,
      pathWithoutLastIndex,
      updatedParentExpressionArray,
    );
  }

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function ungroupActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id } = action.payload;
  let existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const indices = getNumericPathIndices(expressionPath); // grab only digits from the path string
  if (indices.length === 1) {
    // expression is flat 1-dimen array
    // we basically spread what was in the group into the existing expression, at the index where the group was located at
    existingExpression = [
      ...existingExpression.slice(0, Number(indices.at(-1))),
      ...existingExpression[Number(indices.at(-1))] as ExpressionTerm[],
      ...existingExpression.slice(
        Number(indices.at(-1)) + 1) || [],
    ];
  } else {
    const {
      pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
    } = getParentPathAndExpressionItem({
      indices,
      existingExpression,
    });
    // spread what was in the group into the existing expression, at the index where the group was located at
    const updatedParentExpressionArray = [
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex.slice(0, Number(indices.at(-1))),
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex[
        Number(indices.at(-1))] as ExpressionTerm[],
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex.slice(
        Number(indices.at(-1)) + 1) || [],
    ];

    setProperty(
      existingExpression,
      pathWithoutLastIndex,
      updatedParentExpressionArray,
    );
  }

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

